import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Badge, Button, Card, Form, Nav, Spinner } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateTimeEsMX } from '../../utils/apiDateFormat'

const URL = '/notifications/notifications/'

const ESTADO_BADGE = {
  nueva: ['danger', 'Nueva'],
  leida: ['secondary', 'Leída'],
  cerrada: ['light', 'Cerrada'],
}

const listaDe = (data) => (Array.isArray(data) ? data : data?.results ?? [])

/** Un aviso. Se marca leído al ABRIRLO, no al verlo en la lista. */
const Aviso = ({ aviso, abierto, onAbrir, onCerrar, onResponder, ocupado }) => {
  const [texto, setTexto] = useState(aviso.respuesta || '')
  const [variante, etiqueta] = ESTADO_BADGE[aviso.estado] ?? ['secondary', aviso.estado]
  const cerrada = aviso.estado === 'cerrada'
  const faltaResponder = aviso.requiere_respuesta && !(aviso.respuesta || '').trim()

  return (
    <Card className={`mb-2 ${aviso.estado === 'nueva' ? 'border-danger' : ''}`}>
      <Card.Header
        role="button"
        onClick={() => onAbrir(aviso)}
        className="d-flex justify-content-between align-items-start gap-2"
      >
        <span>
          <Badge bg={variante} text={variante === 'light' ? 'dark' : undefined} className="me-2">
            {etiqueta}
          </Badge>
          <strong>{aviso.subject}</strong>
          {aviso.cliente_nombre && (
            <span className="text-muted small ms-2">· {aviso.cliente_nombre}</span>
          )}
        </span>
        <span className="text-muted small text-nowrap">
          {formatDateTimeEsMX(aviso.created_at)}
        </span>
      </Card.Header>

      {abierto && (
        <Card.Body>
          <p className="mb-3" style={{ whiteSpace: 'pre-wrap' }}>{aviso.message}</p>

          {aviso.enlace && (
            <p className="mb-3">
              <Link to={aviso.enlace}>Ir a donde se resuelve →</Link>
            </p>
          )}

          {aviso.requiere_respuesta && (
            <Form.Group className="mb-3">
              <Form.Label>
                Respuesta {faltaResponder && <span className="text-danger">*</span>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={texto}
                disabled={cerrada}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Escribe aquí tu respuesta"
              />
              {!cerrada && (
                <Button
                  size="sm"
                  className="mt-2"
                  disabled={ocupado || !texto.trim()}
                  onClick={() => onResponder(aviso, texto)}
                >
                  Guardar respuesta
                </Button>
              )}
              {aviso.respondida_at && (
                <div className="text-muted small mt-1">
                  Respondida el {formatDateTimeEsMX(aviso.respondida_at)}
                </div>
              )}
            </Form.Group>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted small">
              {aviso.read_at && <>Leída el {formatDateTimeEsMX(aviso.read_at)}. </>}
              {aviso.cerrada_at && <>Cerrada el {formatDateTimeEsMX(aviso.cerrada_at)}.</>}
            </span>
            {!cerrada && (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={ocupado || faltaResponder}
                title={faltaResponder ? 'Este aviso pide una respuesta antes de cerrarse' : ''}
                onClick={() => onCerrar(aviso)}
              >
                Cerrar
              </Button>
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  )
}

const Notifications = () => {
  const [seccion, setSeccion] = useState('abiertas')
  const [abierto, setAbierto] = useState(null)
  const queryClient = useQueryClient()

  const params = seccion === 'abiertas' ? { abiertas: 'true' } : { estado: 'cerrada' }
  const { data, isLoading } = useQuery(
    ['notificaciones', seccion],
    async () => {
      const { data } = await api.get(URL, { params })
      return listaDe(data)
    }
  )

  const refrescar = () => {
    queryClient.invalidateQueries('notificaciones')
    queryClient.invalidateQueries('notificaciones-nuevas')
  }

  const accion = useMutation(
    ({ id, ruta, body }) => api.post(`${URL}${id}/${ruta}/`, body),
    {
      onSuccess: refrescar,
      onError: (err) => toast.error(
        err.response?.data?.detail || 'No se pudo completar la acción'),
    }
  )

  const onAbrir = (aviso) => {
    const yaEstaba = abierto === aviso.id
    setAbierto(yaEstaba ? null : aviso.id)
    // Abrir el aviso es lo que lo marca leido. Verlo en la lista no cuenta.
    if (!yaEstaba && aviso.estado === 'nueva') {
      accion.mutate({ id: aviso.id, ruta: 'marcar_leida' })
    }
  }

  const avisos = data ?? []

  return (
    <div className="mt-4">
      <h1 className="h4 mb-3">Notificaciones</h1>

      <Nav variant="tabs" activeKey={seccion} onSelect={(k) => { setSeccion(k); setAbierto(null) }}>
        <Nav.Item><Nav.Link eventKey="abiertas">Pendientes</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="historial">Historial</Nav.Link></Nav.Item>
      </Nav>

      <div className="pt-3">
        {isLoading && <Spinner animation="border" size="sm" />}

        {!isLoading && avisos.length === 0 && (
          <Alert variant="light" className="border">
            {seccion === 'abiertas'
              ? 'No tienes avisos pendientes.'
              : 'Todavía no has cerrado ningún aviso.'}
          </Alert>
        )}

        {avisos.map((aviso) => (
          <Aviso
            key={aviso.id}
            aviso={aviso}
            abierto={abierto === aviso.id}
            ocupado={accion.isLoading}
            onAbrir={onAbrir}
            onCerrar={(a) => accion.mutate({ id: a.id, ruta: 'cerrar' })}
            onResponder={(a, texto) => accion.mutate({
              id: a.id, ruta: 'responder', body: { respuesta: texto },
            })}
          />
        ))}
      </div>
    </div>
  )
}

export default Notifications
